import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogAbsenteeBidders } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryPromoted,
    getItemSummarySaleStartTs,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogBidders, getLiveCatalogPaused } from '@/redux/modules/console';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { shouldShowLive } from '@/utils/live';
import { useAppSelector } from '@/redux/hooks';
import ConsoleSocialBadge from '@/components/ConsoleSocial/ConsoleSocialBadge';

export type ItemCardBadgeProps = {
    allowBidding?: boolean;
    className?: string;
    itemId: number;
    myItemsSavedItemCard?: boolean;
    pushSoldBadgeToLeftCorner?: boolean;
    savedItemsCard?: boolean;
};

const ItemCardBadge = ({
    allowBidding,
    className,
    itemId,
    myItemsSavedItemCard,
    pushSoldBadgeToLeftCorner,
    savedItemsCard,
}: ItemCardBadgeProps) => {
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const paused = useAppSelector((state) => getLiveCatalogPaused(state, catalogId));
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const showSocial = allowBidding && showLive && !isSold && !isPassed;
    const viewers = bidders + absenteeBidders || 1;

    return (
        <>
            {promoted && !isSold && (
                <Badge
                    className={cn(
                        'absolute',
                        'z-[1]',
                        {
                            'mobile:left-2xs mobile:top-2xs xsMax:left-[6px] xsMax:top-[6px]': myItemsSavedItemCard,
                        },
                        className
                    )}
                    color="neutral"
                    data-testid="featured-badge"
                    round
                >
                    <FormattedMessage id="featured" />
                </Badge>
            )}
            {isSold && !isPassed && (
                <Badge
                    className={cn(
                        'absolute left-xs top-xs z-[2]',
                        {
                            'xsMax:left-2xs xsMax:top-2xs': pushSoldBadgeToLeftCorner,
                        },
                        className
                    )}
                    color="neutral"
                    data-testid="sold-badge"
                    emphasis="bold"
                    size="sm"
                >
                    <FormattedMessage id="similarSoldItemCarousel.badge" />
                </Badge>
            )}
            {showSocial && !isSold && !isUnsold && (
                <ConsoleSocialBadge
                    className={cn(
                        'absolute top-0 m-[10px]',
                        {
                            'ml-xs mt-xs xsMax:ml-2xs xsMax:pr-[6px]': myItemsSavedItemCard,
                            'ml-xs mt-xs xsMax:ml-xs xsMax:mt-[12px]': savedItemsCard,
                        },
                        className
                    )}
                    myItemsSavedItemsCard={myItemsSavedItemCard}
                    pause={paused}
                    viewers={viewers}
                />
            )}
        </>
    );
};

export default ItemCardBadge;
